import { useCallback, useEffect, useState } from 'react';

const COLLAPSED_KEY = 'navCollapsed';
const COLLAPSED_EVENT = 'sidenavcollapsechange';

/*
 * Whether the sidebar is collapsed to its icon rail.
 *
 * Persisted, because the tables here are wide and someone who works collapsed
 * wants to stay that way. Broadcast through a window event so every consumer
 * stays in step without the layout threading state between them.
 */
function read(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === 'true';
  } catch {
    // Private browsing and blocked storage both throw on access.
    return false;
  }
}

function write(value: boolean) {
  try {
    window.localStorage.setItem(COLLAPSED_KEY, String(value));
  } catch {
    // The preference simply will not persist; not worth failing a render over.
  }

  window.dispatchEvent(new Event(COLLAPSED_EVENT));
}

export default function useSideNav() {
  const [isCollapsed, setCollapsed] = useState<boolean>(read);

  useEffect(() => {
    const sync = () => setCollapsed(read());

    window.addEventListener(COLLAPSED_EVENT, sync);
    window.addEventListener('storage', sync);

    return () => {
      window.removeEventListener(COLLAPSED_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const toggleCollapsed = useCallback(() => write(!read()), []);

  return { isCollapsed, toggleCollapsed };
}
